// src/components/ChatInterface.jsx
import { useState, useRef, useEffect } from 'react';
import { Send } from 'lucide-react';
import { useApiOperations } from '@utils/api-functions';
import { debug } from '@utils/debug'; // Import debug utility

/**
 * Renders a chat interface allowing users to interact with an AI assistant
 * about the user's professional background (derived from profile data).
 * Handles message display, user input, API calls, loading states, and basic error handling.
 * @returns {JSX.Element} The chat interface component.
 */
const ChatInterface = () => {
  const { sendChatMessage } = useApiOperations();
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hi! I'm an AI assistant that can tell you about my professional background. Feel free to ask about my experience, skills, or specific roles!"
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  /**
   * Scrolls the message container view to the bottom smoothly.
   * @private
   */
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  /**
   * Handles the submission of a new chat message.
   * Adds the user's message to the state optimistically, clears the input,
   * calls the `sendChatMessage` API, and adds the assistant's response or an error message.
   * @param {React.FormEvent<HTMLFormElement>} e - The form submission event.
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim()) return;

    const newUserMessage = { role: 'user', content: inputMessage };
    setMessages(prevMessages => [...prevMessages, newUserMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const data = await sendChatMessage(inputMessage);
      const assistantMessage = { role: 'assistant', content: data.response };
      setMessages(prevMessages => [...prevMessages, assistantMessage]);
    } catch (error) {
      debug('chat:error', 'Error sending chat message:', error);
      const errorMessage = { role: 'assistant', content: "I'm sorry, I encountered an error. Please try again." };
      // Note: We already added the user message optimistically.
      // We need to ensure we don't add it again here if the API call fails.
      // The current state already includes the user message added before the try block.
      setMessages(prevMessages => [...prevMessages, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-5rem)] w-full mx-auto p-4">
      <div className="flex-1 overflow-y-auto mb-4 space-y-4 bg-white rounded-lg p-6 shadow-soft">
        {messages.map((message, index) => (
          <div
            key={index}
            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[80%] p-3 rounded-lg text-base ${message.role === 'user'
                  ? 'bg-huntd-info text-white'
                  : 'bg-huntd-light text-huntd-primary'
                }`}
            >
              {message.content.split('\n').map((line, i) => (
                <p key={i} className="mb-1 last:mb-0">
                  {line}
                </p>
              ))}
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-huntd-light text-huntd-primary p-3 rounded-lg max-w-[80%] text-base flex items-center">
              <span className="mr-2">Thinking...</span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={handleSubmit} className="flex gap-2">
        <input
          type="text"
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          placeholder="Ask me anything about my professional background..."
          className="flex-1 p-3 border border-huntd-secondary/30 rounded-lg focus:outline-none focus:border-huntd-info text-base"
          disabled={isLoading}
        />
        <button
          type="submit"
          disabled={isLoading}
          className="p-3 bg-huntd-info text-white rounded-lg hover:bg-huntd-info/80 disabled:bg-huntd-info/50 disabled:cursor-not-allowed border border-huntd-info"
        >
          <Send className="w-5 h-5" />
        </button>
      </form>
    </div>
  );
};

export default ChatInterface;
